/**
 * Which document this is, and which bid it belongs to.
 *
 * Both live in the document's own settings (`Office.context.document.settings`),
 * which travel inside the .docx: reopening the file on another machine, or
 * a colleague opening it from SharePoint, reconnects to the same threads and
 * bid without asking. The id is minted the first time the pane sees a
 * document. Word on the web partitions `localStorage` per top-level site,
 * so nothing here relies on it.
 */

import { v4 as uuid } from "uuid";

export const DOCUMENT_ID_SETTING = "mytender_document_id";
export const BID_ID_SETTING = "mytender_bid_id";

export type DocumentIdentity = {
  documentId: string;
  bidId: string | null;
};

const settingsAvailable = (): boolean => {
  try {
    return Boolean(Office?.context?.document?.settings);
  } catch {
    return false;
  }
};

const saveSettings = (): Promise<void> =>
  new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(new Error(result.error?.message ?? "Could not save document settings"));
    });
  });

// Outside Office (the pane opened in a plain browser tab) the identity is
// kept for the page's lifetime only, so the UI still works for development.
let fallback: DocumentIdentity | null = null;

/** The page-lifetime identity used when the document's settings can't be read. */
export const fallbackDocumentIdentity = (): DocumentIdentity => {
  fallback ??= { documentId: uuid(), bidId: null };
  return fallback;
};

/**
 * Read the document's identity, minting an id on first sight.
 * @returns The document id and the linked bid, if any.
 */
export const loadDocumentIdentity = async (): Promise<DocumentIdentity> => {
  if (!settingsAvailable()) return fallbackDocumentIdentity();
  const settings = Office.context.document.settings;
  let documentId = settings.get(DOCUMENT_ID_SETTING) as string | null;
  const bidId = (settings.get(BID_ID_SETTING) as string | null) || null;
  if (!documentId) {
    documentId = uuid();
    settings.set(DOCUMENT_ID_SETTING, documentId);
    try {
      await saveSettings();
    } catch (error) {
      // A read-only document cannot keep the id; the pane still works, the
      // threads just will not follow the file.
      console.warn("Could not persist the document id", error);
    }
  }
  return { documentId, bidId };
};

/**
 * Link (or unlink) the document to a bid.
 * @param bidId - The bid, or null to unlink.
 */
export const saveLinkedBid = async (bidId: string | null): Promise<void> => {
  if (!settingsAvailable()) {
    if (fallback) fallback = { ...fallback, bidId };
    return;
  }
  const settings = Office.context.document.settings;
  if (bidId) settings.set(BID_ID_SETTING, bidId);
  else settings.remove(BID_ID_SETTING);
  await saveSettings();
};

export const TEMPLATE_SETTING = "mytender_template";

export type TemplateStepStatus = "applied" | "skipped" | "unsupported" | "error";

/** A style the template uses for one kind of block: the id `w:pStyle` takes, and the name Word shows. */
export type TemplateStyleRef = { id: string; name: string };

/**
 * Which of the template's styles its own body pages use, from the server's
 * reading of the file, so content the add-in writes later (pulled answers,
 * io.ai's drafts) takes the template's look rather than Normal and Heading 1.
 */
export type TemplateStyleMap = {
  body: TemplateStyleRef;
  /** `has_numbering`: the style carries its own bullet, so no typed bullet is needed. */
  bullet: (TemplateStyleRef & { has_numbering: boolean }) | null;
  number: (TemplateStyleRef & { has_numbering: boolean }) | null;
  /** By outline level, "1".."4". */
  headings: Record<string, TemplateStyleRef>;
  table: TemplateStyleRef | null;
  /** The heading after which the response goes, when the template has one ("Start of Tender Response"). */
  response_start: string | null;
};

export type AppliedTemplate = {
  fileId: string;
  name: string;
  appliedAt: string;
  /** How each step ended, so applying the same template again redoes only what failed. */
  steps?: Partial<Record<"styles" | "body" | "page_setup" | "headers_footers", TemplateStepStatus>>;
  /** The style names the template imported, so a later template can put back the ones it does not define. */
  styleNames?: string[];
  /** A hash of the text on the inserted frame pages, to find them again if their control is gone. */
  coverHash?: string;
  /** How many paragraphs the front frame held. */
  coverParagraphs?: number;
  /** The styles the template's body uses (see {@link TemplateStyleMap}). */
  styleMap?: TemplateStyleMap;
};

export const TEMPLATE_STYLE_SNAPSHOT_SETTING = "mytender_template_style_snapshot";

/**
 * The document's own definitions of the styles a template overwrote, taken
 * before the first template touched them (Word's style JSON, one entry per
 * style). A later template that does not define one of them puts the
 * original back, so template B over template A looks like B on a fresh
 * document rather than a layer over A.
 */
export type StyleSnapshotEntry = Record<string, unknown> & { nameLocal: string };

export const loadStyleSnapshot = async (): Promise<StyleSnapshotEntry[]> => {
  if (!settingsAvailable()) return [];
  try {
    const raw = Office.context.document.settings.get(TEMPLATE_STYLE_SNAPSHOT_SETTING) as string | null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as StyleSnapshotEntry[]) : [];
  } catch {
    return [];
  }
};

/** Keep at most this many entries: document settings travel inside the file. */
const STYLE_SNAPSHOT_LIMIT = 60;

export const saveStyleSnapshot = async (entries: StyleSnapshotEntry[]): Promise<void> => {
  if (!settingsAvailable()) return;
  const settings = Office.context.document.settings;
  if (entries.length)
    settings.set(TEMPLATE_STYLE_SNAPSHOT_SETTING, JSON.stringify(entries.slice(0, STYLE_SNAPSHOT_LIMIT)));
  else settings.remove(TEMPLATE_STYLE_SNAPSHOT_SETTING);
  await saveSettings();
};

/** The template last applied to this document, if any. */
export const loadAppliedTemplate = async (): Promise<AppliedTemplate | null> => {
  if (!settingsAvailable()) return null;
  try {
    const raw = Office.context.document.settings.get(TEMPLATE_SETTING) as string | null;
    return raw ? (JSON.parse(raw) as AppliedTemplate) : null;
  } catch {
    return null;
  }
};

/** Remember the template applied to this document (null to forget). */
export const saveAppliedTemplate = async (template: AppliedTemplate | null): Promise<void> => {
  if (!settingsAvailable()) return;
  const settings = Office.context.document.settings;
  if (template) settings.set(TEMPLATE_SETTING, JSON.stringify(template));
  else settings.remove(TEMPLATE_SETTING);
  await saveSettings();
};
